import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Grid,
    Tooltip,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Autocomplete,
    Select,
    MenuItem
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Download } from '@mui/icons-material';

import { RootState } from '../../../store';
import { MobileAppUserTypes } from '../../../types';
import { MobileAppUserService } from '../../../services/mobile-app-user.service';
// import { useDebounce } from '../../../hooks/useDebounce';

interface Props {
    onFilter(filters: MobileAppUserTypes.Filters): Promise<void>;
    onDownload(): Promise<void>;
}

export const MobileAppUsersFilters: React.FC<Props> = ({ onFilter, onDownload }) => {
    const [open, setOpen] = useState(false);
    const [customers, setCustomers] = useState<MobileAppUserTypes.CustomerForFilter[]>([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    const { loading } = useSelector((state: RootState) => state.mobileAppUser);

    const initial: MobileAppUserTypes.Filters = {
        search: '',
        appStatus: undefined,
        appVersion: '',
        osVersion: '',
    };

    const formik = useFormik<MobileAppUserTypes.Filters>({
        initialValues: { ...initial },
        onSubmit: async (filters) => {
            await onFilter(filters);
        }
    });

    const { 
        values,
        setFieldValue,
        handleSubmit,
    } = formik;

    // Debounced search to avoid too many API calls
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (values.search !== undefined) {
                onFilter({ ...values, search: values.search });
            }
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [values.search]);

    // Debounced app version filter
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (values.appVersion !== undefined) {
                onFilter({ ...values, appVersion: values.appVersion });
            }
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [values.appVersion]);

    // Debounced OS version filter
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (values.osVersion !== undefined) {
                onFilter({ ...values, osVersion: values.osVersion });
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [values.osVersion]);

    useEffect(() => {
        const fetchCustomers = async () => {
            setLoadingCustomers(true);
            const customerData = await MobileAppUserService.getCustomersForFilter();
            setCustomers(customerData);
            setLoadingCustomers(false);
        };
        
        fetchCustomers();
    }, []);

    const selectedCustomer = customers.find(c => c.subscriptionId === values.subscriptionId) || null;

    return (
        <Box sx={{ borderRadius: 0 }}>
            <Accordion expanded={open}>
                <AccordionSummary
                    onClick={() => setOpen(o => !o)}
                    expandIcon={
                        <Grid container display="flex" flexDirection={"row"}>
                            <Tooltip title={open ? "Hide Filters" : "Show Filters"}>
                                <ExpandMoreIcon/>
                            </Tooltip>
                        </Grid>
                    }
                >
                    <FilterListIcon sx={{ mr: 1.5, ml: -1 }} />
                    <Typography fontSize={16} fontWeight={500}>
                        Search & Filters
                    </Typography>
                    <Grid container mt={1} display={'flex'} flexDirection={'row-reverse'}>
                        <Tooltip title={"Download Mobile App Users List"}>
                            <Grid mr={1.2}>
                                <Download onClick={() => {
                                    onDownload();
                                    setOpen(false);
                                }} />
                            </Grid>
                        </Tooltip>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Search"
                                        placeholder="Search by phone number, first name, or last name..."
                                        value={values.search}
                                        onChange={(e) => setFieldValue('search', e.target.value)}
                                        size="medium"
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        fullWidth
                                        loading={loadingCustomers}
                                        options={customers}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedCustomer}
                                        onChange={(_, newValue) => {
                                            setFieldValue('subscriptionId', newValue?.subscriptionId || '');
                                            if (newValue) {
                                                onFilter({ ...values, subscriptionId: newValue.subscriptionId });
                                            } else {
                                                // Clear the customer filter - don't send subscriptionId at all
                                                const { subscriptionId, ...filtersWithoutSubscriptionId } = values;
                                                onFilter(filtersWithoutSubscriptionId);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Filter by Customer"
                                                placeholder="Select a customer..."
                                                variant="outlined"
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props}>
                                                <Box>
                                                    <Typography variant="body2">
                                                        {option.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>App Status</InputLabel>
                                        <Select
                                            value={values.appStatus || ''}
                                            onChange={(e) => {
                                                const newValue = e.target.value || undefined;
                                                setFieldValue('appStatus', newValue);
                                                onFilter({ ...values, appStatus: newValue as 'ON' | 'OFF' | undefined });
                                            }}
                                            label="App Status"
                                        >
                                            <MenuItem value="">None (All)</MenuItem>
                                            <MenuItem value="ON">ON</MenuItem>
                                            <MenuItem value="OFF">OFF</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="App Version"
                                        placeholder="e.g., 1.0.0, 2.3.1..."
                                        value={values.appVersion}
                                        onChange={(e) => setFieldValue('appVersion', e.target.value)}
                                        size="medium"
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="OS Version"
                                        placeholder="e.g., 18.3.1, 14..."
                                        value={values.osVersion}
                                        onChange={(e) => setFieldValue('osVersion', e.target.value)}
                                        size="medium"
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                            <Grid display={'flex'} flexDirection={'row-reverse'} mt={2}>
                                <Button
                                    disabled={loading?.fetchMany}
                                    variant='text'
                                    sx={{ mr: 1 }}
                                    color={'inherit'}
                                    onClick={async () => {
                                        formik.setValues({
                                            ...initial
                                        });
                                        await onFilter({ search: '', appStatus: undefined, appVersion: '', osVersion: '' });
                                    }}
                                >
                                    Clear filters
                                </Button>
                            </Grid>
                        </Form>
                    </FormikProvider>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
